import ModalLayout from "@/components/layout/ModalLayout";
import { InputBox } from "@/components/ui/InputBox";
import { Button } from "@/components/ui/Button";
import { useState, useCallback, useEffect } from "react";
import ModalHeader from "@/components/ui/ModalHeader";
import { useCodeListByVehicleGb1, useCodeListByVehicleGb2 } from "@/hooks/standard/st_code/useCode";
import SelectBox from "@/components/ui/SelectBox";
import SearchModal from "./Search";
import { useDriverCode } from "@/hooks/standard/st_driver_code/useDriverCode";
import { useCompanyCode } from "@/hooks/standard/st_company_code/useCompanyCode";
import { VehicleCode } from "@/interface/standard/st_vehicle_code";
import { useInsertVehicleCode } from "@/hooks/standard/st_vehicle_code/useVehicleCode";
import { toast } from "sonner";

interface AddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: VehicleCode) => void;
}

export default function AddModal({ isOpen, onClose, onSave }: AddModalProps) {
  const insertVehicleCode = useInsertVehicleCode();
  const INITIAL_FORM_DATA: VehicleCode = {
    stVehicleCode: "",
    stVehicleNumber1: "",
    stVehicleNumber2: "",
    stVehicleGb1: "",
    stVehicleGb2: "",
    stVehicleShape: "",
    stVehicleLicenseKind: "",
    stVehicleLicenseItem: "",
    stVehicleLicenseCapa: "",
    stVehicleInstitution: "",
    stVehicleMaxCapa: "",
    stVehicleLocation: "",
    stVehicleRemark: "",
    stVehicleDrivercode: "",
    stVehicleCustcode: "",
    stVehicleEmploymentStatus: "Y",
    stDriverCode: "",
    stCompanyCode: "",
    driverName: "",
    mobileTel: "",
    companyName: "",
    driverSearchQuery: "",
    companySearchQuery: "",
  };
  const [isDriverSearchOpen, setIsDriverSearchOpen] = useState(false);
  const [isCompanySearchOpen, setIsCompanySearchOpen] = useState(false);
  // 검색어를 분리
  const [driverSearchKeyword, setDriverSearchKeyword] = useState("");
  const [companySearchKeyword, setCompanySearchKeyword] = useState("");
  const [isLoadingMoreDriver, setIsLoadingMoreDriver] = useState(false);
  const [isLoadingMoreCompany, setIsLoadingMoreCompany] = useState(false);
  // 각각의 페이지 상태로 분리
  const [currentDriverPage, setCurrentDriverPage] = useState(0);
  const [currentCompanyPage, setCurrentCompanyPage] = useState(0);
  const [allDriverData, setAllDriverData] = useState<any[]>([]);
  const [allCompanyData, setAllCompanyData] = useState<any[]>([]);
  const { data: driverSearchData } = useDriverCode({
    page: currentDriverPage,
    size: 20,
    search: driverSearchKeyword,
    sort: "stDriverCode,desc",
  });
  const { data: companySearchData } = useCompanyCode({
    page: currentCompanyPage,
    size: 20,
    search: companySearchKeyword,
    sort: "stCompanyCode,desc",
  });
  const { data: vehicleGb1Data } = useCodeListByVehicleGb1();
  const { data: vehicleGb2Data } = useCodeListByVehicleGb2();
  const [formData, setFormData] = useState<VehicleCode>(INITIAL_FORM_DATA);

  // 데이터 누적 로직 추가
  useEffect(() => {
    if (driverSearchData?.content && driverSearchData.content.length > 0) {
      if (currentDriverPage === 0) {
        setAllDriverData(driverSearchData.content);
      } else {
        setAllDriverData(prev => [...prev, ...driverSearchData.content]);
      }
    }
  }, [driverSearchData, currentDriverPage]);

  useEffect(() => {
    if (companySearchData?.content && companySearchData.content.length > 0) {
      if (currentCompanyPage === 0) {
        setAllCompanyData(companySearchData.content);
      } else {
        setAllCompanyData(prev => [...prev, ...companySearchData.content]);
      }
    }
  }, [companySearchData, currentCompanyPage]);

  const handleDriverSelect = (item: any) => {
    setFormData(prev => ({
      ...prev,
      stDriverCode: item.stDriverCode || prev.stDriverCode,
      stVehicleDrivercode: item.stDriverCode || prev.stVehicleDrivercode,
      driverName: item.stDriverName || prev.driverName,
      mobileTel: item.stDriverMobileTel || prev.mobileTel,
    }));
    setIsDriverSearchOpen(false);
  };

  const handleCompanySelect = (item: any) => {
    setFormData(prev => ({
      ...prev,
      stCompanyCode: item.stCompanyCode || prev.stCompanyCode,
      stVehicleCustcode: item.stCompanyCode || prev.stVehicleCustcode,
      companyName: item.stCompanyName || prev.companyName,
    }));
    setIsCompanySearchOpen(false);
  };

  const driverSearchColumns = [
    { key: "stDriverName", header: "기사명", bodyAlign: "center" as const, headerAlign: "center" as const },
    { key: "stDriverMobileTel", header: "기사전화번호", bodyAlign: "center" as const, headerAlign: "center" as const },
  ];

  const companySearchColumns = [
    { key: "stCompanyName", header: "업체명", bodyAlign: "center" as const, headerAlign: "center" as const },
    { key: "stCompanyNumber", header: "사업자번호", bodyAlign: "center" as const, headerAlign: "center" as const },
    { key: "stCompanyOwnername", header: "대표자", bodyAlign: "center" as const, headerAlign: "center" as const },
  ];

  const handleInputChange = (field: keyof VehicleCode, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    setFormData(INITIAL_FORM_DATA);
    onClose();
  };

  const handleSave = async () => {
    if (!formData.stVehicleNumber1.trim()) {
      toast.warning("실차량번호를 입력해주세요.");
      return;
    }
    if (!formData.stVehicleNumber2.trim()) {
      toast.warning("가상차량번호를 입력해주세요.");
      return;
    }
    if (!formData.stVehicleGb1.trim()) {
      toast.warning("위탁구분을 선택해주세요.");
      return;
    }
    if (!formData.stVehicleGb2.trim()) {
      toast.warning("차량구분을 선택해주세요.");
      return;
    }
    if (!formData.stVehicleDrivercode.trim()) {
      toast.warning("담당 기사를 추가해주세요.");
      return;
    }
    if (!formData.stVehicleCustcode.trim()) {
      toast.warning("담당 업체를 추가해주세요.");
      return;
    }
    try {
      await insertVehicleCode.mutateAsync({
        stVehicleNumber1: formData.stVehicleNumber1,
        stVehicleNumber2: formData.stVehicleNumber2,
        stVehicleGb1: formData.stVehicleGb1,
        stVehicleGb2: formData.stVehicleGb2,
        stVehicleShape: formData.stVehicleShape,
        stVehicleLicenseKind: formData.stVehicleLicenseKind,
        stVehicleLicenseItem: formData.stVehicleLicenseItem,
        stVehicleLicenseCapa:
          formData.stVehicleLicenseCapa && formData.stVehicleLicenseCapa.trim()
            ? formData.stVehicleLicenseCapa.toString()
            : undefined,
        stVehicleInstitution: formData.stVehicleInstitution,
        stVehicleMaxCapa:
          formData.stVehicleMaxCapa && formData.stVehicleMaxCapa.trim()
            ? formData.stVehicleMaxCapa.toString()
            : undefined,
        stVehicleLocation: formData.stVehicleLocation,
        stVehicleRemark: formData.stVehicleRemark,
        stVehicleDrivercode: formData.stVehicleDrivercode || formData.stDriverCode,
        stVehicleCustcode: formData.stVehicleCustcode || formData.stCompanyCode,
        stVehicleEmploymentStatus: formData.stVehicleEmploymentStatus || "Y",
      });
      handleCancel();
      onSave(formData);
    } catch (error: any) {
      console.error("저장 실패:", error);
    }
  };

  // 기사 검색 무한스크롤 핸들러
  const handleDriverInfiniteScroll = useCallback(() => {
    const hasMoreDriver = !(driverSearchData?.last ?? true) || false;
    if (hasMoreDriver && !isLoadingMoreDriver) {
      setIsLoadingMoreDriver(true);
      setCurrentDriverPage(prev => prev + 1);
    }
  }, [driverSearchData?.last, isLoadingMoreDriver]);

  // 업체 검색 무한스크롤 핸들러
  const handleCompanyInfiniteScroll = useCallback(() => {
    const hasMoreCompany = !(companySearchData?.last ?? true) || false;
    if (hasMoreCompany && !isLoadingMoreCompany) {
      setIsLoadingMoreCompany(true);
      setCurrentCompanyPage(prev => prev + 1);
    }
  }, [companySearchData?.last, isLoadingMoreCompany]);

  // 기사 검색 핸들러
  const handleDriverSearch = useCallback((keyword: string) => {
    setDriverSearchKeyword(keyword);
    setIsLoadingMoreDriver(false);
    setCurrentDriverPage(0);
    setAllDriverData([]);
  }, []);

  // 업체 검색 핸들러
  const handleCompanySearch = useCallback((keyword: string) => {
    setCompanySearchKeyword(keyword);
    setIsLoadingMoreCompany(false);
    setCurrentCompanyPage(0);
    setAllCompanyData([]);
  }, []);

  // 데이터가 갱신되면 로딩 플래그 해제
  useEffect(() => {
    if (isLoadingMoreDriver) setIsLoadingMoreDriver(false);
  }, [driverSearchData, isLoadingMoreDriver]);

  useEffect(() => {
    if (isLoadingMoreCompany) setIsLoadingMoreCompany(false);
  }, [companySearchData, isLoadingMoreCompany]);

  // 기사 검색 모달이 닫히면 초기화
  useEffect(() => {
    if (!isDriverSearchOpen) {
      setDriverSearchKeyword("");
      setCurrentDriverPage(0);
      setAllDriverData([]);
      setIsLoadingMoreDriver(false);
    }
  }, [isDriverSearchOpen]);

  // 업체 검색 모달이 닫히면 초기화
  useEffect(() => {
    if (!isCompanySearchOpen) {
      setCompanySearchKeyword("");
      setCurrentCompanyPage(0);
      setAllCompanyData([]);
      setIsLoadingMoreCompany(false);
    }
  }, [isCompanySearchOpen]);

  return (
    <ModalLayout isOpen={isOpen} onClose={handleCancel} title="차량코드 추가">
      <div className="rounded-lg">
        <ModalHeader title="기본 정보" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <InputBox
              required={true}
              label="실차량번호"
              className="w-full"
              value={formData.stVehicleNumber1}
              onChange={e => handleInputChange("stVehicleNumber1", e.target.value)}
              placeholder="실차량번호 입력"
            />
          </div>
          <div>
            <InputBox
              required={true}
              label="가상차량번호"
              className="w-full"
              value={formData.stVehicleNumber2}
              onChange={e => handleInputChange("stVehicleNumber2", e.target.value)}
              placeholder="가상차량번호 입력"
            />
          </div>
          <div>
            <SelectBox
              label="사용여부"
              className="w-full"
              value={formData.stVehicleEmploymentStatus || "Y"}
              onChange={value => handleInputChange("stVehicleEmploymentStatus", value)}
              options={[
                { value: "Y", label: "사용" },
                { value: "N", label: "미사용" },
              ]}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <InputBox
              label="기사명"
              className="w-full"
              value={formData.driverName}
              placeholder="기사명 검색"
              readOnly
              required={true}
              showIcon
              onIconClick={() => setIsDriverSearchOpen(true)}
            />
          </div>
          <div>
            <InputBox
              label="기사전화번호"
              className="w-full"
              value={formData.mobileTel}
              placeholder="기사전화번호"
              readOnly
            />
          </div>
          <div>
            <InputBox
              label="업체명"
              className="w-full"
              value={formData.companyName}
              placeholder="업체명 검색"
              readOnly
              required={true}
              showIcon
              onIconClick={() => setIsCompanySearchOpen(true)}
            />
          </div>
        </div>
        <ModalHeader title="추가 정보" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <SelectBox
              required={true}
              label="위탁구분"
              className="w-full"
              value={formData.stVehicleGb1}
              onChange={value => handleInputChange("stVehicleGb1", value)}
              placeholder="선택"
              options={vehicleGb1Data?.map(item => ({ value: item.code, label: item.name })) || []}
            />
          </div>
          <div>
            <SelectBox
              required={true}
              label="차량구분"
              className="w-full"
              value={formData.stVehicleGb2}
              onChange={value => handleInputChange("stVehicleGb2", value)}
              placeholder="선택"
              options={vehicleGb2Data?.map(item => ({ value: item.code, label: item.name })) || []}
            />
          </div>
          <div>
            <InputBox
              label="차량형태"
              className="w-full"
              value={formData.stVehicleShape}
              onChange={e => handleInputChange("stVehicleShape", e.target.value)}
              placeholder="차량형태 입력"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <InputBox
              label="허가류"
              className="w-full"
              value={formData.stVehicleLicenseKind}
              onChange={e => handleInputChange("stVehicleLicenseKind", e.target.value)}
              placeholder="허가류 입력"
            />
          </div>
          <div>
            <InputBox
              label="허가항목"
              className="w-full"
              value={formData.stVehicleLicenseItem}
              onChange={e => handleInputChange("stVehicleLicenseItem", e.target.value)}
              placeholder="허가항목 입력"
            />
          </div>
          <div>
            <InputBox
              label="허가용량"
              className="w-full"
              value={formData.stVehicleLicenseCapa}
              onChange={e => handleInputChange("stVehicleLicenseCapa", e.target.value)}
              placeholder="허가용량 입력"
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <InputBox
            label="최대용량"
            className="w-full"
            value={formData.stVehicleMaxCapa}
            onChange={e => handleInputChange("stVehicleMaxCapa", e.target.value)}
            placeholder="최대용량 입력"
          />
        </div>
        <div>
          <InputBox
            label="차고지"
            className="w-full"
            value={formData.stVehicleInstitution}
            onChange={e => handleInputChange("stVehicleInstitution", e.target.value)}
            placeholder="차고지 입력"
          />
        </div>
        <div>
          <InputBox
            label="차고지 주소"
            className="w-full"
            value={formData.stVehicleLocation}
            onChange={e => handleInputChange("stVehicleLocation", e.target.value)}
            placeholder="차고지 주소 입력"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-4">
        <div>
          <InputBox
            label="비고"
            className="w-full"
            value={formData.stVehicleRemark}
            onChange={e => handleInputChange("stVehicleRemark", e.target.value)}
            placeholder="비고 입력"
          />
        </div>
      </div>
      {/* 모달 푸터 */}
      <div className="flex items-center gap-3 mt-6">
        <div className="flex justify-start w-full">
          <Button variant="white" onClick={handleCancel} className="py-[10px]">
            취소
          </Button>
        </div>
        <div className="flex justify-end w-full">
          <Button variant="primary" onClick={handleSave} className="py-[10px]">
            저장
          </Button>
        </div>
      </div>
      <SearchModal
        isOpen={isDriverSearchOpen}
        onClose={() => setIsDriverSearchOpen(false)}
        onSelect={handleDriverSelect}
        title="기사 검색"
        columns={driverSearchColumns}
        data={allDriverData}
        isLoadingInitial={false}
        isLoadingMore={isLoadingMoreDriver}
        hasMore={!(driverSearchData?.last ?? true)}
        onSearch={handleDriverSearch}
        onSearchButtonClick={handleDriverSearch}
        onInfiniteScroll={handleDriverInfiniteScroll}
        initialSearchKeyword={formData.driverSearchQuery}
        keyword={driverSearchKeyword}
      />
      <SearchModal
        isOpen={isCompanySearchOpen}
        onClose={() => setIsCompanySearchOpen(false)}
        onSelect={handleCompanySelect}
        title="업체 검색"
        columns={companySearchColumns}
        data={allCompanyData}
        isLoadingInitial={false}
        isLoadingMore={isLoadingMoreCompany}
        hasMore={!(companySearchData?.last ?? true)}
        onSearch={handleCompanySearch}
        onSearchButtonClick={handleCompanySearch}
        onInfiniteScroll={handleCompanyInfiniteScroll}
        initialSearchKeyword={formData.companySearchQuery}
        keyword={companySearchKeyword}
      />
    </ModalLayout>
  );
}
